import {Button, Field, FieldProps, Input, Radio, RadioGroup, Spinner} from "@fluentui/react-components";
import {ShoppingFunnelDataTransferObject} from "../../../../model/dto/content/ShoppingFunnelDataTransferObject";
import {Column, Row} from "../../common/Layout";
import {DataTransferObjectValidation, Types} from "../../../../type/Types";
import {
    InputStringStatePair,
    StatePair,
    useError,
    useInputString,
    useStatePair,
    useUndefined
} from "../../../../type/States";
import {Styles} from "../../common/Styles";
import {ShoppingFunnelValueObject} from "../../../../model/vo/content/ShoppingFunnelValueObject";
import {RouteContext} from "../../../RouteContext";
import {ShoppingFunnelIO} from "../../../../io/content/ShoppingFunnelIO";
import {useParams} from "react-router-dom";
import {useEffect} from "react";

export default function Edit(props: RouteContext) {
    const params = useParams<{ id: string }>()
    const itemState = useUndefined<ShoppingFunnelValueObject>()
    const itemErrorState = useError()

    useEffect(() => {
        if (params.id === undefined) {
            return
        }
        if (itemState.value !== undefined) {
            return
        }

        ShoppingFunnelIO.get(BigInt(params.id))
            .then(itemState.setDefinedValue)
            .catch(itemErrorState.setDefinedValue)
    }, [params.id, itemState.value])

    const item = itemState.value
    return <>
        <itemErrorState.Dialog {...props} />
        {item === undefined && <Spinner />}
        {item !== undefined && <>
            <EditPage
                {...props}
                item={item}></EditPage>
        </>}
    </>
}

type EditPageProps = RouteContext & {
    item: ShoppingFunnelValueObject
}

function EditPage(props: EditPageProps) {
    const styles = Styles.useEditorStyles()
    const inputStates: Record<keyof ShoppingFunnelDataTransferObject.Put, InputStringStatePair> = {
        shoppingId: useInputString(Types.present(props.item.shoppingId, '')),
        adminId: useInputString(Types.present(props.item.adminId, '')),
        isActive: useInputString(Types.present(props.item.isActive, '') === 'O' ? 'true' : 'false'),
        exposedAt: useInputString(Types.present(props.item.exposedAt, '')),
        catalogRank: useInputString(Types.present(props.item.catalogRank, '')),
        mid: useInputString(Types.present(props.item.mid, '')),
        reviewCount: useInputString(Types.present(props.item.reviewCount, '')),
        reviewScore: useInputString(Types.present(props.item.reviewScore, '')),
        mallCount: useInputString(Types.present(props.item.mallCount, '')),
        storeName: useInputString(Types.present(props.item.storeName, '')),
        productName: useInputString(Types.present(props.item.productName, '')),
        productImageUrl: useInputString(Types.present(props.item.productImageUrl, '')),
        productNumber: useInputString(Types.present(props.item.productNumber, '')),
        answer: useInputString(Types.present(props.item.answer, '')),
        productPrice: useInputString(Types.present(props.item.productPrice, '')),
        productUrl: useInputString(Types.present(props.item.productUrl, '')),
        mainKeywordString: useInputString(Types.present(props.item.mainKeywordString, '')),
        mallSequence: useInputString(Types.present(props.item.mallSequence, '')),
        categoryId: useInputString(Types.present(props.item.categoryId, '')),
        catalogMid: useInputString(Types.present(props.item.catalogMid, '')),
        catalogProductName: useInputString(Types.present(props.item.catalogProductName, '')),
        catalogLowestPrice: useInputString(Types.present(props.item.catalogLowestPrice, '')),
        catalogDiscountedPrice: useInputString(Types.present(props.item.catalogDiscountedPrice, '')),
        catalogDeliveryFee: useInputString(Types.present(props.item.catalogDeliveryFee, '')),
        amount: useInputString(Types.present(props.item.amount, '')),
        amountBeforeRefresh: useInputString(Types.present(props.item.amountBeforeRefresh, '')),
        successCount: useInputString(Types.present(props.item.successCount, '')),
        failureCount: useInputString(Types.present(props.item.failureCount, '')),
        reportCount: useInputString(Types.present(props.item.reportCount, '')),
        postBackSuccessCount: useInputString(Types.present(props.item.postBackSuccessCount, '')),
        missionClickCount: useInputString(Types.present(props.item.missionClickCount, '')),
        clipBoardClickCount: useInputString(Types.present(props.item.clipBoardClickCount, '')),
        landingCount: useInputString(Types.present(props.item.landingCount, '')),
        deactivatedPathJointCount: useInputString(Types.present(props.item.deactivatedPathJointCount, '')),
    }
    const inputValidationStates: Record<keyof ShoppingFunnelDataTransferObject.Put, StatePair<FieldProps['validationState']>> = {
        shoppingId: useStatePair<FieldProps['validationState']>('none'),
        adminId: useStatePair<FieldProps['validationState']>('none'),
        isActive: useStatePair<FieldProps['validationState']>('none'),
        exposedAt: useStatePair<FieldProps['validationState']>('none'),
        catalogRank: useStatePair<FieldProps['validationState']>('none'),
        mid: useStatePair<FieldProps['validationState']>('none'),
        reviewCount: useStatePair<FieldProps['validationState']>('none'),
        reviewScore: useStatePair<FieldProps['validationState']>('none'),
        mallCount: useStatePair<FieldProps['validationState']>('none'),
        storeName: useStatePair<FieldProps['validationState']>('none'),
        productName: useStatePair<FieldProps['validationState']>('none'),
        productImageUrl: useStatePair<FieldProps['validationState']>('none'),
        productNumber: useStatePair<FieldProps['validationState']>('none'),
        answer: useStatePair<FieldProps['validationState']>('none'),
        productPrice: useStatePair<FieldProps['validationState']>('none'),
        productUrl: useStatePair<FieldProps['validationState']>('none'),
        mainKeywordString: useStatePair<FieldProps['validationState']>('none'),
        mallSequence: useStatePair<FieldProps['validationState']>('none'),
        categoryId: useStatePair<FieldProps['validationState']>('none'),
        catalogMid: useStatePair<FieldProps['validationState']>('none'),
        catalogProductName: useStatePair<FieldProps['validationState']>('none'),
        catalogLowestPrice: useStatePair<FieldProps['validationState']>('none'),
        catalogDiscountedPrice: useStatePair<FieldProps['validationState']>('none'),
        catalogDeliveryFee: useStatePair<FieldProps['validationState']>('none'),
        amount: useStatePair<FieldProps['validationState']>('none'),
        amountBeforeRefresh: useStatePair<FieldProps['validationState']>('none'),
        successCount: useStatePair<FieldProps['validationState']>('none'),
        failureCount: useStatePair<FieldProps['validationState']>('none'),
        reportCount: useStatePair<FieldProps['validationState']>('none'),
        postBackSuccessCount: useStatePair<FieldProps['validationState']>('none'),
        missionClickCount: useStatePair<FieldProps['validationState']>('none'),
        clipBoardClickCount: useStatePair<FieldProps['validationState']>('none'),
        landingCount: useStatePair<FieldProps['validationState']>('none'),
        deactivatedPathJointCount: useStatePair<FieldProps['validationState']>('none'),
    }
    const putErrorState = useError()

    const onDoneClick = () => {
        inputValidationStates.shoppingId.setValue('none')
        inputValidationStates.adminId.setValue('none')
        inputValidationStates.isActive.setValue('none')
        inputValidationStates.exposedAt.setValue('none')
        inputValidationStates.catalogRank.setValue('none')
        inputValidationStates.mid.setValue('none')
        inputValidationStates.reviewCount.setValue('none')
        inputValidationStates.reviewScore.setValue('none')
        inputValidationStates.mallCount.setValue('none')
        inputValidationStates.storeName.setValue('none')
        inputValidationStates.productName.setValue('none')
        inputValidationStates.productImageUrl.setValue('none')
        inputValidationStates.productNumber.setValue('none')
        inputValidationStates.answer.setValue('none')
        inputValidationStates.productPrice.setValue('none')
        inputValidationStates.productUrl.setValue('none')
        inputValidationStates.mallSequence.setValue('none')
        inputValidationStates.categoryId.setValue('none')
        inputValidationStates.catalogMid.setValue('none')
        inputValidationStates.catalogProductName.setValue('none')
        inputValidationStates.catalogLowestPrice.setValue('none')
        inputValidationStates.catalogDiscountedPrice.setValue('none')
        inputValidationStates.catalogDeliveryFee.setValue('none')
        inputValidationStates.mainKeywordString.setValue('none')
        inputValidationStates.amount.setValue('none')
        inputValidationStates.amountBeforeRefresh.setValue('none')
        inputValidationStates.successCount.setValue('none')
        inputValidationStates.failureCount.setValue('none')
        inputValidationStates.reportCount.setValue('none')
        inputValidationStates.postBackSuccessCount.setValue('none')
        inputValidationStates.missionClickCount.setValue('none')
        inputValidationStates.clipBoardClickCount.setValue('none')
        inputValidationStates.landingCount.setValue('none')
        inputValidationStates.deactivatedPathJointCount.setValue('none')
        try {
            const dto: ShoppingFunnelDataTransferObject.Put = {
                shoppingId: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'shoppingId', inputStates.shoppingId.value, () => inputValidationStates.shoppingId.setValue('error')),
                adminId: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'adminId', inputStates.adminId.value, () => inputValidationStates.adminId.setValue('error')),
                isActive: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'isActive', inputStates.isActive.value, () => inputValidationStates.isActive.setValue('error')),
                exposedAt: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'exposedAt', inputStates.exposedAt.value, () => inputValidationStates.exposedAt.setValue('error')),
                catalogRank: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'catalogRank', inputStates.catalogRank.value, () => inputValidationStates.catalogRank.setValue('error')),
                mid: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'mid', inputStates.mid.value, () => inputValidationStates.mid.setValue('error')),
                reviewCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'reviewCount', inputStates.reviewCount.value, () => inputValidationStates.reviewCount.setValue('error')),
                reviewScore: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'reviewScore', inputStates.reviewScore.value, () => inputValidationStates.reviewScore.setValue('error')),
                mallCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'mallCount', inputStates.mallCount.value, () => inputValidationStates.mallCount.setValue('error')),
                storeName: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'storeName', inputStates.storeName.value, () => inputValidationStates.storeName.setValue('error')),
                productName: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'productName', inputStates.productName.value, () => inputValidationStates.productName.setValue('error')),
                productImageUrl: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'productImageUrl', inputStates.productImageUrl.value, () => inputValidationStates.productImageUrl.setValue('error')),
                productNumber: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'productNumber', inputStates.productNumber.value, () => inputValidationStates.productNumber.setValue('error')),
                answer: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'answer', inputStates.answer.value, () => inputValidationStates.answer.setValue('error')),
                productPrice: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'productPrice', inputStates.productPrice.value, () => inputValidationStates.productPrice.setValue('error')),
                productUrl: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'productUrl', inputStates.productUrl.value, () => inputValidationStates.productUrl.setValue('error')),
                mainKeywordString: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'mainKeywordString', inputStates.mainKeywordString.value, () => inputValidationStates.mainKeywordString.setValue('error')),
                mallSequence: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'mallSequence', inputStates.mallSequence.value, () => inputValidationStates.mallSequence.setValue('error')),
                categoryId: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'categoryId', inputStates.categoryId.value, () => inputValidationStates.categoryId.setValue('error')),
                catalogMid: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'catalogMid', inputStates.catalogMid.value, () => inputValidationStates.catalogMid.setValue('error')),
                catalogProductName: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'catalogProductName', inputStates.catalogProductName.value, () => inputValidationStates.catalogProductName.setValue('error')),
                catalogLowestPrice: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'catalogLowestPrice', inputStates.catalogLowestPrice.value, () => inputValidationStates.catalogLowestPrice.setValue('error')),
                catalogDiscountedPrice: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'catalogDiscountedPrice', inputStates.catalogDiscountedPrice.value, () => inputValidationStates.catalogDiscountedPrice.setValue('error')),
                catalogDeliveryFee: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'catalogDeliveryFee', inputStates.catalogDeliveryFee.value, () => inputValidationStates.catalogDeliveryFee.setValue('error')),
                amount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'amount', inputStates.amount.value, () => inputValidationStates.amount.setValue('error')),
                amountBeforeRefresh: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'amountBeforeRefresh', inputStates.amountBeforeRefresh.value, () => inputValidationStates.amountBeforeRefresh.setValue('error')),
                successCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'successCount', inputStates.successCount.value, () => inputValidationStates.successCount.setValue('error')),
                failureCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'failureCount', inputStates.failureCount.value, () => inputValidationStates.failureCount.setValue('error')),
                reportCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'reportCount', inputStates.reportCount.value, () => inputValidationStates.reportCount.setValue('error')),
                postBackSuccessCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'postBackSuccessCount', inputStates.postBackSuccessCount.value, () => inputValidationStates.postBackSuccessCount.setValue('error')),
                missionClickCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'missionClickCount', inputStates.missionClickCount.value, () => inputValidationStates.missionClickCount.setValue('error')),
                clipBoardClickCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'clipBoardClickCount', inputStates.clipBoardClickCount.value, () => inputValidationStates.clipBoardClickCount.setValue('error')),
                landingCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'landingCount', inputStates.landingCount.value, () => inputValidationStates.landingCount.setValue('error')),
                deactivatedPathJointCount: DataTransferObjectValidation.validate(ShoppingFunnelDataTransferObject.Put.validation, 'deactivatedPathJointCount', inputStates.deactivatedPathJointCount.value, () => inputValidationStates.deactivatedPathJointCount.setValue('error')),

            }

            ShoppingFunnelIO.put(props.item.id, dto)
                .then(() => window.alert('수정되었습니다.'))
                .catch(putErrorState.setDefinedValue)
        } catch (error) {
            console.error(error)
        }
    }

    return <>
        <putErrorState.Dialog {...props} />
        <Column gap={'16px'}>
            <RadioGroup
                value={inputStates.isActive.value}
                onChange={
                    (_, data) => {inputStates.isActive.setValue(data.value); console.log(inputStates.isActive.value)}
                }>
                <Radio value="true" label="활성화" />
                <Radio value="false" label="비활성화" />
            </RadioGroup>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.shoppingId}
                validationState={inputValidationStates.shoppingId.value}>
                <Input
                    value={inputStates.shoppingId.value}
                    onChange={inputStates.shoppingId.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.adminId}
                validationState={inputValidationStates.adminId.value}>
                <Input
                    value={inputStates.adminId.value}
                    onChange={inputStates.adminId.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.exposedAt}
                validationState={inputValidationStates.exposedAt.value}>
                <Input
                    value={inputStates.exposedAt.value}
                    onChange={inputStates.exposedAt.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.catalogRank}
                validationState={inputValidationStates.catalogRank.value}>
                <Input
                    value={inputStates.catalogRank.value}
                    onChange={inputStates.catalogRank.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.mid}
                validationState={inputValidationStates.mid.value}>
                <Input
                    value={inputStates.mid.value}
                    onChange={inputStates.mid.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.reviewCount}
                validationState={inputValidationStates.reviewCount.value}>
                <Input
                    value={inputStates.reviewCount.value}
                    onChange={inputStates.reviewCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.reviewScore}
                validationState={inputValidationStates.reviewScore.value}>
                <Input
                    value={inputStates.reviewScore.value}
                    onChange={inputStates.reviewScore.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.mallCount}
                validationState={inputValidationStates.mallCount.value}>
                <Input
                    value={inputStates.mallCount.value}
                    onChange={inputStates.mallCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.storeName}
                validationState={inputValidationStates.storeName.value}>
                <Input
                    value={inputStates.storeName.value}
                    onChange={inputStates.storeName.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.productName}
                validationState={inputValidationStates.productName.value}>
                <Input
                    value={inputStates.productName.value}
                    onChange={inputStates.productName.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.productImageUrl}
                validationState={inputValidationStates.productImageUrl.value}>
                <Input
                    value={inputStates.productImageUrl.value}
                    onChange={inputStates.productImageUrl.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.productNumber}
                validationState={inputValidationStates.productNumber.value}>
                <Input
                    value={inputStates.productNumber.value}
                    onChange={inputStates.productNumber.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.answer}
                validationState={inputValidationStates.answer.value}>
                <Input
                    value={inputStates.answer.value}
                    onChange={inputStates.answer.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.productPrice}
                validationState={inputValidationStates.productPrice.value}>
                <Input
                    value={inputStates.productPrice.value}
                    onChange={inputStates.productPrice.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.productUrl}
                validationState={inputValidationStates.productUrl.value}>
                <Input
                    value={inputStates.productUrl.value}
                    onChange={inputStates.productUrl.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.mainKeywordString}
                validationState={inputValidationStates.mainKeywordString.value}>
                <Input
                    value={inputStates.mainKeywordString.value}
                    onChange={inputStates.mainKeywordString.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.mallSequence}
                validationState={inputValidationStates.mallSequence.value}>
                <Input
                    value={inputStates.mallSequence.value}
                    onChange={inputStates.mallSequence.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.categoryId}
                validationState={inputValidationStates.categoryId.value}>
                <Input
                    value={inputStates.categoryId.value}
                    onChange={inputStates.categoryId.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.catalogMid}
                validationState={inputValidationStates.catalogMid.value}>
                <Input
                    value={inputStates.catalogMid.value}
                    onChange={inputStates.catalogMid.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.catalogProductName}
                validationState={inputValidationStates.catalogProductName.value}>
                <Input
                    value={inputStates.catalogProductName.value}
                    onChange={inputStates.catalogProductName.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.catalogLowestPrice}
                validationState={inputValidationStates.catalogLowestPrice.value}>
                <Input
                    value={inputStates.catalogLowestPrice.value}
                    onChange={inputStates.catalogLowestPrice.onChange} />
            </Field>
            {/*<Field*/}
            {/*    className={styles.field}*/}
            {/*    label={ShoppingFunnelDataTransferObject.Put.description.catalogDiscountedPrice}*/}
            {/*    validationState={inputValidationStates.catalogDiscountedPrice.value}>*/}
            {/*    <Input*/}
            {/*        value={inputStates.catalogDiscountedPrice.value}*/}
            {/*        onChange={inputStates.catalogDiscountedPrice.onChange} />*/}
            {/*</Field>*/}
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.catalogDeliveryFee}
                validationState={inputValidationStates.catalogDeliveryFee.value}>
                <Input
                    value={inputStates.catalogDeliveryFee.value}
                    onChange={inputStates.catalogDeliveryFee.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.amount}
                validationState={inputValidationStates.amount.value}>
                <Input
                    value={inputStates.amount.value}
                    onChange={inputStates.amount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.amountBeforeRefresh}
                validationState={inputValidationStates.amountBeforeRefresh.value}>
                <Input
                    value={inputStates.amountBeforeRefresh.value}
                    onChange={inputStates.amountBeforeRefresh.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.successCount}
                validationState={inputValidationStates.successCount.value}>
                <Input
                    value={inputStates.successCount.value}
                    onChange={inputStates.successCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.failureCount}
                validationState={inputValidationStates.failureCount.value}>
                <Input
                    value={inputStates.failureCount.value}
                    onChange={inputStates.failureCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.reportCount}
                validationState={inputValidationStates.reportCount.value}>
                <Input
                    value={inputStates.reportCount.value}
                    onChange={inputStates.reportCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.postBackSuccessCount}
                validationState={inputValidationStates.postBackSuccessCount.value}>
                <Input
                    value={inputStates.postBackSuccessCount.value}
                    onChange={inputStates.postBackSuccessCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.missionClickCount}
                validationState={inputValidationStates.missionClickCount.value}>
                <Input
                    value={inputStates.missionClickCount.value}
                    onChange={inputStates.missionClickCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.clipBoardClickCount}
                validationState={inputValidationStates.clipBoardClickCount.value}>
                <Input
                    value={inputStates.clipBoardClickCount.value}
                    onChange={inputStates.clipBoardClickCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.landingCount}
                validationState={inputValidationStates.landingCount.value}>
                <Input
                    value={inputStates.landingCount.value}
                    onChange={inputStates.landingCount.onChange} />
            </Field>
            <Field
                className={styles.field}
                label={ShoppingFunnelDataTransferObject.Put.description.deactivatedPathJointCount}
                validationState={inputValidationStates.deactivatedPathJointCount.value}>
                <Input
                    value={inputStates.deactivatedPathJointCount.value}
                    onChange={inputStates.deactivatedPathJointCount.onChange} />
            </Field>
            <Row>
                <Button
                    appearance={'primary'}
                    onClick={onDoneClick}>
                    완료
                </Button>
            </Row>
        </Column>
    </>
}